import { FormikProps } from "formik";
import { useMemo, useRef, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Dispatch } from "shared";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { columnsUsersType } from "../types/columsUsers.types";
import {
  getDeductions,
  DeductionItem,
  postDeductionsProviders,
  getDeductionsProviders,
  GetDeductionsProvidersResponse,
  deleteDeductionsProviders,
} from "shared/api/services";

export const useFormAssociateDeduction = (user: columnsUsersType) => {
  const dispatch = useDispatch<Dispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const [deductions, setDeductions] = useState<any[]>([]);
  const [associatedDeductions, setAssociatedDeductions] =
    useState<GetDeductionsProvidersResponse>([]);
  const formikRef = useRef<FormikProps<any>>(null);

  const loadDeductions = async (searchText?: string) => {
    try {
      const response = await getDeductions(
        searchText ? { search: searchText, size: 500 } : { size: 500 },
      );
      const options = response.content.map((item: DeductionItem) => ({
        id: item.id,
        name: `${item.conceptCode} - ${item.conceptDescription} - ${item.percentage}%`,
      }));
      setDeductions(options);
    } catch (error) {
      console.error("Error loading deductions:", error);
    }
  };

  useEffect(() => {
    loadDeductions();
    loadAssociatedDeductions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeductionChange = (value: any) => {
    if (typeof value === "string") {
      // When user types, treat as search text
      loadDeductions(value);
    }
    // When user selects an option, the value will be an object with id
  };

  const loadAssociatedDeductions = async () => {
    if (!user.employerIdNumber || user.employerIdNumber.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "El número de identificación del empleador es requerido",
        confirmButtonText: "Aceptar",
      });
      return;
    }
    try {
      const response = await getDeductionsProviders(user.employerIdNumber);
      setAssociatedDeductions(response);
    } catch (error) {
      console.error("Error loading associated deductions:", error);
    }
  };

  const validateEmployerIdNumber = (): boolean => {
    if (!user.employerIdNumber || user.employerIdNumber.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "El número de identificación del empleador es requerido para asociar deducciones",
        confirmButtonText: "Aceptar",
      });
      return false;
    }
    return true;
  };

  const handleRemoveDeduction = async (deductionId: number) => {
    try {
      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: "¿Deseas retirar esta deducción asociada?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, retirar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        await deleteDeductionsProviders(user.employerIdNumber, deductionId);
        await loadAssociatedDeductions();
        Swal.fire({
          icon: "success",
          title: "Retirada",
          text: "La deducción ha sido retirada correctamente",
          confirmButtonText: "Aceptar",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.response?.data?.message || "Error al retirar la deducción",
      });
    }
  };

  const initialValues = {
    deductions: [],
  };
  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      deductions: Yup.array()
        .min(1, "Debe seleccionar al menos una deducción")
        .required("Las deducciones son obligatorias"),
    });
  }, []);

  const handleSubmit = async (values: any) => {
    setIsLoading(true);
    try {
      const deductionsIds = values.deductions.map((d: any) => d.id);
      await postDeductionsProviders({
        employerIdNumber: user.employerIdNumber,
        deductionsIds: deductionsIds,
      });
      await loadAssociatedDeductions();
      setIsLoading(false);
      Swal.fire({
        icon: "success",
        title: "Deducciones asociadas",
        text: "Las deducciones se han asociado correctamente",
        confirmButtonText: "Aceptar",
      }).then(() => {
        formikRef.current?.resetForm();
        dispatch.messageModal.cleanStateMessage();
      });
    } catch (error: any) {
      setIsLoading(false);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.response?.data?.message || "Error al asociar deducciones",
      });
    }
  };

  return {
    initialValues,
    formikRef,
    isLoading,
    validationSchema,
    handleSubmit,
    deductions,
    handleDeductionChange,
    associatedDeductions,
    loadAssociatedDeductions,
    validateEmployerIdNumber,
    handleRemoveDeduction,
  };
};
